import React, { useState } from 'react'
import { Menu, MenuItem } from '@mui/material';
import { FileResponse, FileSource } from '../interfaces/FileResponse';
import { SheetSource } from './use-sheet-source';
import mainStyles from '../../index.module.css'
import { ShareIcon } from '../icons/icons';

export const SHARE_MESSAGE = 'Essa foi encontrada no partituravip.com.br';

interface ShareButtonProps {
    item: FileResponse,
    source: SheetSource,
}

// Dropbox sheets only exist behind the API, so they have no public link to send
const publicLink = (item: FileResponse) => item.source === FileSource.Dropbox ? '' : item.address;

const fileName = (item: FileResponse, extension: string) => {
    const name = item.address.split('?')[0].split('/').pop() || 'partitura';
    return name.includes('.') ? name : `${name}.${extension}`;
}

// Turns the sheet into a File so phones can send the image itself, not only a link
async function toFile(item: FileResponse, source: SheetSource): Promise<File | undefined> {
    try {
        let blob = source.blob;

        if (!blob) {
            const url = item.source === FileSource.Dropbox ? source.src : item.address;
            if (!url) return undefined;

            blob = await (await fetch(url)).blob();
        }

        const extension = source.extension || item.extension || 'jpg';
        const type = extension === 'pdf' ? 'application/pdf' : `image/${extension === 'jpg' ? 'jpeg' : extension}`;

        return new File([blob], fileName(item, extension), { type });
    } catch {
        // Crawler hosts often block cross-origin downloads; the link is shared instead
        return undefined;
    }
}

export default function ShareButton({ item, source }: ShareButtonProps) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const link = publicLink(item);
    const text = link ? `${SHARE_MESSAGE}\n${link}` : SHARE_MESSAGE;

    const onClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        const target = e.currentTarget;

        // Touch devices open the native share sheet, where WhatsApp and Telegram receive the file itself
        if (navigator.share && window.matchMedia?.('(pointer: coarse)').matches) {
            const file = await toFile(item, source);
            const data: ShareData = file && navigator.canShare?.({ files: [file] })
                ? { files: [file], text: SHARE_MESSAGE }
                : { text };

            try {
                await navigator.share(data);
                return;
            } catch (error: any) {
                if (error?.name === 'AbortError') return;
            }
        }

        setAnchor(target);
    }

    const open = (url: string) => {
        setAnchor(null);
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    const onWhatsApp = () => open(`https://wa.me/?text=${encodeURIComponent(text)}`);

    // Telegram requires a url, so Dropbox sheets point to the portal itself
    const onTelegram = () => open(`https://t.me/share/url?url=${encodeURIComponent(link || 'https://partituravip.com.br')}&text=${encodeURIComponent(SHARE_MESSAGE)}`);

    return <>
        <button type='button' className={mainStyles.buttonSecondary} onClick={onClick}>
            <ShareIcon />
            Compartilhar
        </button>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <MenuItem onClick={onWhatsApp}>WhatsApp</MenuItem>
            <MenuItem onClick={onTelegram}>Telegram</MenuItem>
        </Menu>
    </>
}
